/**
 * Utility functions for handling custom domains and subdomains
 */

/**
 * Get the current hostname
 */
export const getCurrentHostname = (): string => {
  if (typeof window === "undefined") return "";
  return window.location.hostname;
};

/**
 * Check if we're on the main domain (aiserwave.com)
 */
export const isMainDomain = (): boolean => {
  const hostname = getCurrentHostname();

  // Main domain variations
  const mainDomains = [
    "aiserwave.com",
    "www.aiserwave.com",
    "aisewave-app.icyground-9e9982ba.westus2.azurecontainerapps.io",
  ];

  return mainDomains.includes(hostname);
};

/**
 * Extract organization slug from subdomain
 * e.g., "theajstars-beans.aiserwave.com" -> "theajstars-beans"
 * Returns null if not a subdomain or if on main domain
 */
export const getOrganizationSlugFromSubdomain = (): string | null => {
  const hostname = getCurrentHostname();

  // Localhost is not a subdomain
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return null;
  }

  // Check if we're on the main domain
  if (isMainDomain()) {
    return null;
  }

  // Check if hostname ends with .aiserwave.com
  if (hostname.endsWith(".aiserwave.com")) {
    // Extract subdomain (everything before .aiserwave.com)
    const subdomain = hostname.replace(".aiserwave.com", "");

    // Remove www. if present
    const slug = subdomain.replace(/^www\./, "");

    // Return the slug if it's not empty
    return slug || null;
  }

  // For other domains (custom domains), return null
  // Custom domain handling can be added here if needed
  return null;
};

/**
 * Check if the current domain is a subdomain of aiserwave.com
 */
export const isSubdomain = (): boolean => {
  return getOrganizationSlugFromSubdomain() !== null;
};

/**
 * Check if the current domain is a custom domain (not localhost and not the main app domain)
 * This now treats subdomains as "custom domains" for routing purposes
 */
export const isCustomDomain = (): boolean => {
  const hostname = getCurrentHostname();

  // Localhost is not a custom domain
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return false;
  }

  // If we're on the main domain, it's not a custom domain
  if (isMainDomain()) {
    return false;
  }

  // If we're on a subdomain, treat it as a custom domain for routing
  if (isSubdomain()) {
    return true;
  }

  // For other domains (actual custom domains), treat as custom domain
  // The backend will validate if it's actually a registered custom domain
  return true;
};

/**
 * Get the domain string to use for API lookup
 * Handles both www and non-www versions
 */
export const getDomainForLookup = (): string => {
  const hostname = getCurrentHostname();

  // Remove www. prefix for consistency (backend should handle both)
  if (hostname.startsWith("www.")) {
    return hostname.substring(4);
  }

  return hostname;
};

/**
 * Generate the correct event URL based on whether we're on a custom domain
 * @param eventId - The event ID
 * @param organizationSlug - The organization slug (optional, used for non-custom domains)
 * @returns The correct URL path for the event
 */
export const getEventUrl = (
  eventId: string,
  organizationSlug?: string
): string => {
  if (isCustomDomain()) {
    // On custom domain, use simplified route without slug
    return `/event/${eventId}`;
  } else {
    // On regular domain, use slug-based route
    return organizationSlug
      ? `/event/${organizationSlug}/${eventId}`
      : `/event/${eventId}`;
  }
};

/**
 * Generate the correct my-tickets URL based on whether we're on a custom domain
 * @param organizationSlug - The organization slug (optional, used for non-custom domains)
 * @returns The correct URL path for my-tickets
 */
export const getMyTicketsUrl = (organizationSlug?: string): string => {
  if (isCustomDomain()) {
    // On custom domain, use simplified route without slug
    return `/my-tickets`;
  } else {
    // On regular domain, use slug-based route
    return organizationSlug ? `/${organizationSlug}/my-tickets` : `/my-tickets`;
  }
};

/**
 * Generate the correct payment-history URL based on whether we're on a custom domain
 * @param organizationSlug - The organization slug (optional, used for non-custom domains)
 * @returns The correct URL path for payment-history
 */
export const getPaymentHistoryUrl = (organizationSlug?: string): string => {
  if (isCustomDomain()) {
    // On custom domain, use simplified route without slug
    return `/payment-history`;
  } else {
    // On regular domain, use slug-based route
    return organizationSlug
      ? `/${organizationSlug}/payment-history`
      : `/payment-history`;
  }
};
